import FolderIcon from '@mui/icons-material/Folder';
import { Box, Tooltip, Typography } from '@mui/material';
import { TreeItem2Label } from '@mui/x-tree-view';
import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ALL_MODULES_TREE_ITEM_ID } from '../constants/modulesConstants';
import { selectModules } from '../store/selectors';
import { TreeViewItemId } from '@mui/x-tree-view/models';
import { Module } from '../types';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

export interface CustomTreeItemLabelProps {
    itemId: TreeViewItemId;
    itemHasChildren: boolean;
    children: ReactNode;
    searchString: string;
}

const CustomTreeItemLabel: FC<CustomTreeItemLabelProps> = ({
    children,
    itemHasChildren,
    itemId,
    searchString,
    ...restProps
}) => {
    const { t } = useTranslation();
    const modules = useSelector(selectModules);

    const itemObject = useMemo(() => {
        return modules.find((module: Module) => module.pk === Number(itemId));
    }, [modules, itemId]);

    const matches = match(String(children), searchString, { insideWords: true });
    const parts = parse(String(children), matches);

    return (
        <Tooltip
            placement="bottom-end"
            title={
                itemId === ALL_MODULES_TREE_ITEM_ID || (!itemObject['testcases_cnt'] && !itemObject['schemes_cnt'])
                    ? ''
                    : `${t('testcaseWithCount', { count: itemObject['testcases_cnt'] })} and ${t('schemeWithCount', {
                          count: itemObject['schemes_cnt'],
                      })}`
            }
        >
            <TreeItem2Label
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                }}
                {...restProps}
            >
                {itemId !== ALL_MODULES_TREE_ITEM_ID && <FolderIcon sx={{ marginLeft: '8px' }} />}
                <Box sx={{ flex: 1 }}>
                    {itemId === ALL_MODULES_TREE_ITEM_ID ? (
                        <Box sx={{ fontWeight: 600, ml: 1 }}>{children}</Box>
                    ) : (
                        parts.map((part, index) => (
                            <Typography
                                component={'span'}
                                key={index}
                                sx={{
                                    fontWeight: part.highlight ? 700 : 400,
                                }}
                            >
                                {part.text}
                            </Typography>
                        ))
                    )}
                </Box>
            </TreeItem2Label>
        </Tooltip>
    );
};

export default CustomTreeItemLabel;
